import {
  Box,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useMemo, useEffect } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import {
  getListRoles,
  getUsersById,
  getUsersMasterDataList,
  saveUsers,
  deleteUsers,
  getCountryAssetMasterDataList,
  resetPassword
} from "../../api/Services/masterServie";
import { getUsersColumns } from "../../const/AssertData";
import { RolesMaster, UserMaster } from "../../types/assetTypes";
import PrimeReactMultiSelect from "../../Component/MultiSelect/PrimeReactMultiSelect";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import { filter } from "@syncfusion/ej2-react-filemanager";
import Swal from "sweetalert2";
import { PAGESEND, PAGESTART } from "../../api/endpoint";
import { RootState } from "../../store/Reduxstore";

interface UsersProps {
  setPathname: (path: string) => void;
}

const Users: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<UserMaster[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [roles, setRoles] = useState<RolesMaster[]>([]);
  const [selectedRoll, setSelectedRole] = useState<RolesMaster[] | null>(null);
  const userDetails = useSelector((state: RootState) => state.auth)
  const [usersData, setUsersData] = useState<UserMaster>(
    {
      user_id: 0,
      name: "",
      email: null,
      mobile: null,
      password: null,
      role: [],
      dateOfJoining: new Date(),
      countryIds: [],
    }
  );

  // Memoized column definition
  const columns = useMemo(() => getUsersColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <>
          <ReusablePrimeButton
            //<ReusablePrimeButton
            icon="pi pi-pencil"
            className="p-button-rounded p-button-outlined"
            onClick={() => handleEditUsers(rowData.user_id)}
          />
          {/* <ReusablePrimeButton
              icon="pi pi-trash"
              className="p-button-rounded p-button-outlined p-button-danger"
              onClick={() => handleDeleteUsers(rowData.user_id)}
            /> */}
        </>

      ),
    });
  }

  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" }; // Ensure correct structure
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>),
  );


  useEffect(() => {
    if (roles.length === 0) {
      getRoles();
    }
  }, [roles])

  const getRoles = async () => {
    dispatch(setLoading(true));
    try {
      const { data, status } = await getListRoles(1, PAGESEND);
      if (status.code === 200 && data?.length > 0) {
        setRoles(data);
      }
    } catch (error: any) {
      CustomMessage(error?.message, 'error', enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  }



  const handleEditUsers = async (user_id: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getUsersById(user_id);
      let { data, status } = response;
      if (status.code === 200 && data) {
        setUsersData(data);
        setSelectedRole(data.role);
        setOpenDialog(true);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDeleteUsers = async (user_id: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won’t be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        dispatch(setLoading(true));
        try {
          const response = await deleteUsers(user_id);
          if (response.status === 200) {
            onFetchData(PAGESTART, PAGESEND, filterData);


            CustomMessage("Data deleted", "success", enqueueSnackbar);
          } else {
            CustomMessage(
              "Failed to delete the user.",
              "error",
              enqueueSnackbar
            );
          }
        } catch (error: any) {
          CustomMessage(
            error?.message || "Something went wrong!",
            "error",
            enqueueSnackbar
          );
        } finally {
          dispatch(setLoading(false));
        }
      } else {
        CustomMessage("Deletion cancelled", "info", enqueueSnackbar);
      }
    });
  };



  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } =
        await getUsersMasterDataList(page, rows, filter ?? filterData);
      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setUsersData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setUsersData({
      user_id: 0,
      name: "",
      email: null,
      mobile: null,
      password: null,
      role: [],
      dateOfJoining: new Date(),
      countryIds: []
    });
  };

  const [countryOptions, setCountryOptions] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await getCountryAssetMasterDataList(PAGESTART, PAGESEND, {}); // Provide default values
        if (response.status.code === 200) {
          console.log("Fetched countries:", response.data);
          setCountryOptions(response.data.map(countries => ({
            id: countries.currentLocationId,
            name: countries.countryName,
          })));



        } else {
          setCountryOptions([]);
          CustomMessage(response.status.message, "error", enqueueSnackbar);
        }
      } catch (error: any) {
        CustomMessage(error.message, "error", enqueueSnackbar);
      }
    };

    fetchCountries();
  }, []);


  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    const updatedUserData = { ...usersData };
    if (usersData.name === "") {
      CustomMessage("Add the name", "error", enqueueSnackbar);
      return;
    }
    if (!selectedRoll || Array(selectedRoll).length === 0) {
      CustomMessage("select the roll", 'error', enqueueSnackbar);
      return;
    }
    updatedUserData.role = selectedRoll;
    dispatch(setLoading(true));
    try {
      const response = await saveUsers(updatedUserData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {
        CustomMessage(status.message, "success", enqueueSnackbar);
        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const selectedRolesTemplate = (selectedItems: RolesMaster[]) => {
    if (!selectedItems || selectedItems.length === 0) {
      return "Select the role";
    }

    const maxToShow = 4;
    const visibleItems = selectedItems.slice(0, maxToShow).map(item => item.roleName);
    const remainingCount = selectedItems.length - maxToShow;

    return remainingCount > 0
      ? `${visibleItems.join(", ")} + ${remainingCount} more`
      : visibleItems.join(", ");
  };

  const selectedRolesLabel = (value: RolesMaster[] | null) => {
    if (!value || value.length === 0) {
      return "Select the role";
    }

    const maxToShow = 4;
    const visibleItems = value.slice(0, maxToShow).map(item => item.roleName);
    const remainingCount = value.length - maxToShow;

    return remainingCount > 0
      ? `${visibleItems.join(", ")} + ${remainingCount} more`
      : visibleItems.join(", ");
  };

  const handleResetPassword = async () => {
    if (!usersData.user_id) {
      CustomMessage("Invalid user ID", "error", enqueueSnackbar);
      return;
    }


    {
      dispatch(setLoading(true));
      try {
        // 🔥 CALL API TO RESET PASSWORD
        const response = await resetPassword(usersData.user_id); // <-- Send user_id here

        if (response.status.code === 200) {
          CustomMessage("Password reset link sent successfully", "success", enqueueSnackbar);
          handleClear();
          onFetchData(PAGESTART, PAGESEND, filterData);
        } else {
          CustomMessage(response.status.message, "error", enqueueSnackbar);
        }
      } catch (error: any) {
        CustomMessage(error.message, "error", enqueueSnackbar);
      } finally {
        dispatch(setLoading(false));
      }
    }
    ;
  };






  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >


        {userDetails.role.some(
          r => r.roleName === "Admin" || r.roleName === "Masters List Write"
        ) && (
            <Chip
              className="btn-black-outlined mb-3"
              label="New"
              color="warning"
              onClick={() => setOpenDialog(true)}
              icon={<AddIcon />}
              variant="outlined"
            />
          )}

      </div>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log('Double-click row:', rowData);
            handleEditUsers(rowData.user_id);
          }}
        />
      </Box>
      <ReusablePrimeReactDialog
        header="Add User"
        visible={openDialog}
        onHide={handleClear}
        //allowedRoles={["Admin", "Master List Write"]}
        style={{
          overflow: "hidden",         // parent container hidden
          maxHeight: "90vh",          // limit height to viewport
          width: "600px",             // optional: limit width
        }}
        contentStyle={{
          overflowY: "auto",          // enable vertical scrolling
          maxHeight: "75vh",          // content area scrolls if it overflows
          paddingRight: "8px"         // space for scrollbar
        }}
      >
        <form
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault(); // 🔒 prevent form submission
            }
          }}
        >
          <div className="mb-3">
            <h6>Name</h6>
            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={usersData.name}
              name="name"
              onChange={handleChange}
              sx={{ mt: 0 }}
              required
            />
          </div>
          <div className="mb-3">
            <h6>Email</h6>
            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={usersData.email || ""}
              name="email"
              onChange={handleChange}
              sx={{ mt: 0 }}
              required
            />
          </div>
          <div className="mb-3">
            <h6>Mobile</h6>
            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={usersData.mobile || ""}
              name="mobile"
              onChange={handleChange}
              sx={{ mt: 0 }}
            />
          </div>
          <div className="mb-3">
            <h6>Password</h6>
            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={usersData.password || ""}
              name="password"
              type="text"
              onChange={handleChange}
              sx={{ mt: 0 }}
              required
            />
          </div>
          <style>
            {`

              .custom-multiselect-panel .p-multiselect-items {
          display: grid !important;
          grid-template-columns: repeat(3, 1fr);
          gap: 4px;
          align-items: center; /* ✅ vertically center content */
          }

          .custom-multiselect-panel .p-multiselect-item {
          display: flex;
          align-items: center; /* ✅ ensures checkbox and text are aligned */
          gap: 6px; /* optional spacing between checkbox and label */
          }

          `}
          </style>


          <div className="mb-3" style={{ position: "relative", fontWeight: "bold" }}>
            <h6 style={{ marginBottom: 8, fontWeight: "bold" }}>Select Role</h6>
            <PrimeReactMultiSelect
              value={selectedRoll}
              onChange={(e: MultiSelectChangeEvent) => setSelectedRole(e.value)}
              options={roles}
              optionLabel="roleName"
              filter
              placeholder="Select the role"
              appendTo="self"
              panelClassName="custom-multiselect-panel"
              selectedItemsLabel={selectedRolesLabel(selectedRoll)} // ✅ use label override
              className="w-100 md:w-20rem mt-0 bg-[#fcfcfc] px-[14px] py-[16.5px] rounded-[4px] border border-gray-300"
              style={{ minHeight: "40px" }}
            />

          </div>
          <div className="mb-3" style={{ position: "relative", fontWeight: "bold" }}>
            <Box sx={{ mt: 2, mb: 1, fontWeight: "bold", position: "relative" }}>
              <Typography variant="body1" sx={{ mt: 2, mb: 0, fontWeight: "bold" }}>
                Select Countires
              </Typography>
              <MultiSelect
                value={usersData.countryIds}
                options={countryOptions}
                optionLabel="name"
                optionValue="id"
                placeholder="Select Countries"
                style={{ width: "100%", marginTop: "4px" }}
                appendTo="self"
                filter
                onChange={(e: MultiSelectChangeEvent) => {
                  console.log("Selected countries:", e.value);
                  setUsersData((prev) => ({
                    ...prev,
                    countryIds: e.value as number[],
                  }));
                }}

              />

            </Box>
          </div>

          {/* <div className="mb-3">
          <h6>Select Role</h6>
          <PrimeReactMultiSelect
            value={selectedRoll}
            onChange={(e: MultiSelectChangeEvent) => setSelectedRole(e.value)}
            options={roles}
            optionLabel="roleName"
            filter
            placeholder="Select the role"
            maxSelectedLabels={3}
            className="w-100 md:w-20rem mt-0 bg-[#fcfcfc] px-[14px] py-[16.5px] h-[1.4375em] rounded-[4px] border-initial"
          />
          </div> */}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "30px",
            }}
          >

            {/* Cancel */}
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"
              type="button"        // ✅ Prevents form submission
            >
              Cancel
            </ReusablePrimeButton>

            {/* Submit */}
            <ReusablePrimeButton
              className="ms-1"
              severity="info"
              onClick={handleSubmit}
              type="button"        // ✅ Prevents form submission
              allowedRoles={["Admin", "Masters List Write"]}
            >
              Submit
            </ReusablePrimeButton>

            {/* Reset Password */}
            {usersData.user_id !== 0 && (
              <ReusablePrimeButton
                className="ms-1"
                severity="contrast"
                icon="pi pi-refresh"
                title="Reset Password"
                type="button"        // ✅ Prevents form submission
                onClick={handleResetPassword} // 🔥 Sends user_id correctly
              >
                Reset Password
              </ReusablePrimeButton>
            )}
          </div>

        </form>
      </ReusablePrimeReactDialog>
    </>
  );
};

export default Users;
